import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { getCurrentMonthYearBrasilia } from '../utils/dateUtils';

interface DelayedOrder {
  id: string;
  orderNumber: string;
  carrier: string;
  shippingValue: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NewDelayedOrder {
  orderNumber: string;
  carrier: string;
  shippingValue: string;
  description: string;
}

type PeriodType = 'month' | 'day';

// Transportadoras disponíveis para seleção
const carrierMap: { [key: string]: string } = {
  'TOTAL': 'Total',
  'CORREIOS': 'Correios',
  'ECOM': 'ECOM',
  'SEDEX': 'SEDEX',
  'PAC': 'PAC',
  'TOTAL EXPRESS': 'Total Express',
  'LATAM CARGO': 'LATAM Cargo',
  'JET': 'JET',
  'DOM': 'DOM',
  'CLUBE FLEX': 'Clube Flex',
  'EXPRESSO': 'Expresso',
  'J&T': 'J&T',
};

const carrierOptions = [
  'TOTAL',
  'CORREIOS',
  'ECOM',
  'SEDEX',
  'PAC',
  'TOTAL EXPRESS',
  'LATAM CARGO',
  'JET',
  'DOM',
  'CLUBE FLEX',
  'EXPRESSO',
  'J&T',
];

const DelayedOrders: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const today = new Date().toISOString().slice(0, 10);

  const [orders, setOrders] = useState<DelayedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<DelayedOrder | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [stats, setStats] = useState({
    count: 0,
    totalShipping: '0.00',
    carrier: 'ALL',
  });

  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [month, setMonth] = useState(currentMonth.toString());
  const [year, setYear] = useState(currentYear.toString());
  const [selectedDate, setSelectedDate] = useState(today);

  const [newOrderData, setNewOrderData] = useState<NewDelayedOrder>({
    orderNumber: '',
    carrier: 'TOTAL',
    shippingValue: '',
    description: '',
  });

  const canEdit = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPPORT' || currentUser?.role === 'MANAGER';

  const getDateRange = (): { startDate: string; endDate: string } => {
    if (periodType === 'day') {
      return { startDate: selectedDate, endDate: selectedDate };
    }
    const m = parseInt(month);
    const y = parseInt(year);
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getAllDelayedOrders(startDate, endDate);
      setOrders(data);
    } catch (error: any) {
      showSnackbar('Erro ao carregar pedidos atrasados', 'error');
      console.error('Erro ao carregar pedidos atrasados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (carrier?: string) => {
    try {
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getDelayedOrderStats(carrier, startDate, endDate);
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  useEffect(() => {
    loadOrders();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType, month, year, selectedDate]);

  useEffect(() => {
    loadStats(carrierFilter || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierFilter, orders]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Adicionar novo pedido atrasado
  const handleAddOrder = async () => {
    try {
      if (!newOrderData.orderNumber || !newOrderData.carrier || !newOrderData.shippingValue) {
        showSnackbar('Preencha todos os campos obrigatórios', 'warning');
        return;
      }

      await apiService.createDelayedOrder(newOrderData);
      showSnackbar('Pedido atrasado adicionado com sucesso!', 'success');
      setOpenAddDialog(false);
      setNewOrderData({
        orderNumber: '',
        carrier: 'TOTAL',
        shippingValue: '',
        description: '',
      });
      loadOrders();
    } catch (error: any) {
      showSnackbar('Erro ao adicionar pedido atrasado', 'error');
      console.error('Erro ao adicionar pedido atrasado:', error);
    }
  };

  // Editar pedido atrasado
  const handleEditClick = (order: DelayedOrder) => {
    setEditingOrder(order);
    setOpenEditDialog(true);
  };

  const handleUpdateOrder = async () => {
    if (!editingOrder) return;

    try {
      await apiService.updateDelayedOrder(editingOrder.id, {
        orderNumber: editingOrder.orderNumber,
        carrier: editingOrder.carrier,
        shippingValue: editingOrder.shippingValue,
        description: editingOrder.description || '',
      });

      showSnackbar('Pedido atrasado atualizado com sucesso!', 'success');
      setOpenEditDialog(false);
      setEditingOrder(null);
      loadOrders();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar pedido atrasado', 'error');
      console.error('Erro ao atualizar pedido atrasado:', error);
    }
  };

  // Deletar pedido atrasado
  const handleDeleteOrder = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este pedido atrasado?')) {
      return;
    }

    try {
      await apiService.deleteDelayedOrder(id);
      showSnackbar('Pedido atrasado deletado com sucesso!', 'success');
      loadOrders();
    } catch (error: any) {
      showSnackbar('Erro ao deletar pedido atrasado', 'error');
      console.error('Erro ao deletar pedido atrasado:', error);
    }
  };

  // Deletar múltiplos pedidos atrasados
  const handleDeleteMultiple = async () => {
    if (selectedOrders.size === 0) {
      showSnackbar('Selecione pelo menos um pedido atrasado', 'warning');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedOrders.size} pedidos atrasados?`)) {
      return;
    }

    try {
      await apiService.deleteMultipleDelayedOrders(Array.from(selectedOrders));
      showSnackbar(`${selectedOrders.size} pedidos atrasados deletados com sucesso!`, 'success');
      setSelectedOrders(new Set());
      loadOrders();
    } catch (error: any) {
      showSnackbar('Erro ao deletar pedidos atrasados', 'error');
      console.error('Erro ao deletar pedidos atrasados:', error);
    }
  };

  // Seleção de múltiplos
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(filteredOrders.map(order => order.id));
      setSelectedOrders(allIds);
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedOrders(newSelected);
  };

  // Filtros
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.carrier.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCarrier = !carrierFilter || order.carrier === carrierFilter;

    return matchesSearch && matchesCarrier;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
            Pedidos Atrasados
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Gerencie e acompanhe pedidos atrasados por transportadora
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              label="Período"
            >
              <MenuItem value="month">Mês inteiro</MenuItem>
              <MenuItem value="day">Dia específico</MenuItem>
            </Select>
          </FormControl>

          {periodType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mês</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">
                  <MenuItem value="1">Janeiro</MenuItem>
                  <MenuItem value="2">Fevereiro</MenuItem>
                  <MenuItem value="3">Março</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Maio</MenuItem>
                  <MenuItem value="6">Junho</MenuItem>
                  <MenuItem value="7">Julho</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Setembro</MenuItem>
                  <MenuItem value="10">Outubro</MenuItem>
                  <MenuItem value="11">Novembro</MenuItem>
                  <MenuItem value="12">Dezembro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Ano">
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {periodType === 'day' && (
            <TextField
              size="small"
              label="Data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                backgroundColor: '#D4AF37',
                color: '#000',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#B8941F',
                },
              }}
            >
              Adicionar Atraso
            </Button>
          )}
        </Box>
      </Box>

      {/* Estatísticas */}
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Total de Atrasos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {stats.count}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Valor Total Frete
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                R$ {stats.totalShipping}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por número de pedido ou transportadora"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Transportadora</InputLabel>
                <Select
                  value={carrierFilter}
                  onChange={(e) => setCarrierFilter(e.target.value)}
                  label="Transportadora"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {carrierOptions.map(carrier => (
                    <MenuItem key={carrier} value={carrier}>
                      {carrierMap[carrier]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadOrders}
                sx={{
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  height: '56px',
                  '&:hover': {
                    borderColor: '#B8941F',
                    backgroundColor: 'rgba(212, 175, 55, 0.04)',
                  },
                }}
              >
                Atualizar
              </Button>
            </Grid>
            {canEdit && selectedOrders.size > 0 && (
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteMultiple}
                  sx={{ height: '56px' }}
                >
                  Deletar ({selectedOrders.size})
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
          <Table sx={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)', height: 70, '& > th': { py: 3 } }}>
                {canEdit && (
                  <TableCell sx={{ width: 50, textAlign: 'center', px: 1 }}>
                    <Checkbox
                      checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                      indeterminate={selectedOrders.size > 0 && selectedOrders.size < filteredOrders.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>Nº PEDIDO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>TRANSPORTADORA</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>VALOR DO FRETE</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 2 }}>OCORRÊNCIA</TableCell>
                {canEdit && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, textAlign: 'center', flex: 1 }}>AÇÕES</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 4} align="center" sx={{ py: 4 }}>
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 4} align="center" sx={{ py: 4 }}>
                    Nenhum pedido atrasado encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.02)' },
                      height: 70,
                    }}
                  >
                    {canEdit && (
                      <TableCell sx={{ textAlign: 'center', px: 1 }}>
                        <Checkbox
                          checked={selectedOrders.has(order.id)}
                          onChange={() => handleSelectOne(order.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShippingIcon sx={{ fontSize: 18, color: '#666' }} />
                        <Typography variant="body2">
                          {carrierMap[order.carrier] || order.carrier}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#f59e0b' }}>
                        R$ {order.shippingValue}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 2 }}>
                      <Typography variant="body2" sx={{ color: '#444', whiteSpace: 'pre-wrap' }}>
                        {order.description || '—'}
                      </Typography>
                    </TableCell>
                    {canEdit && (
                      <TableCell sx={{ px: 1, textAlign: 'center', flex: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(order)}
                            sx={{ color: '#D4AF37', mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOrder(order.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog: Adicionar Pedido Atrasado */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Adicionar Pedido Atrasado
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Número do Pedido"
                value={newOrderData.orderNumber}
                onChange={(e) => setNewOrderData({ ...newOrderData, orderNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Transportadora</InputLabel>
                <Select
                  value={newOrderData.carrier}
                  onChange={(e) => setNewOrderData({ ...newOrderData, carrier: e.target.value })}
                  label="Transportadora"
                >
                  {carrierOptions.map(carrier => (
                    <MenuItem key={carrier} value={carrier}>
                      {carrierMap[carrier]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Valor do Frete"
                value={newOrderData.shippingValue}
                onChange={(e) => setNewOrderData({ ...newOrderData, shippingValue: e.target.value })}
                placeholder="0.00"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ocorrência"
                value={newOrderData.description}
                onChange={(e) => setNewOrderData({ ...newOrderData, description: e.target.value })}
                placeholder="Descreva a ocorrência do atraso..."
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddOrder}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Pedido Atrasado */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Editar Pedido Atrasado
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingOrder && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Número do Pedido"
                  value={editingOrder.orderNumber}
                  onChange={(e) => setEditingOrder({ ...editingOrder, orderNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Transportadora</InputLabel>
                  <Select
                    value={editingOrder.carrier}
                    onChange={(e) => setEditingOrder({ ...editingOrder, carrier: e.target.value })}
                    label="Transportadora"
                  >
                    {carrierOptions.map(carrier => (
                      <MenuItem key={carrier} value={carrier}>
                        {carrierMap[carrier]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Valor do Frete"
                  value={editingOrder.shippingValue}
                  onChange={(e) => setEditingOrder({ ...editingOrder, shippingValue: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ocorrência"
                  value={editingOrder.description || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, description: e.target.value })}
                  placeholder="Descreva a ocorrência do atraso..."
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateOrder}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DelayedOrders;
